/** My tickets, and — at /requests — my service requests.
 *
 * One component for both. A request and an incident differ by `type` and by
 * nothing else an employee cares about, so two screens would have been two
 * copies of one list that drift the first time somebody improves one.
 *
 * ## Filters are URL state, not component state
 *
 * The home screen's activity cards link straight to `?filter=mine`, so the
 * filter has to survive a link, a refresh and a back button. Keeping it in the
 * query string also means "the four tickets waiting on me" is a thing an
 * employee can bookmark, which is the whole point of that card.
 */
import { useQuery } from '@tanstack/react-query'
import { Inbox, Search, TicketPlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { Skeleton, Tabs, TabsList, TabsTrigger } from '../../components/ui/primitives'
import { api } from '../../lib/api'
import type { TicketSummary } from '../../lib/types'
import { awaitingIt, byRecentActivity, isOpen, isRequest, needsMyReply } from '../activity'
import { TicketRow } from '../components/TicketRow'

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'mine', label: 'Needs my reply' },
  { key: 'awaiting', label: 'With IT' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
] as const

type FilterKey = (typeof FILTERS)[number]['key']

const PREDICATES: Record<FilterKey, (ticket: TicketSummary) => boolean> = {
  open: isOpen,
  mine: needsMyReply,
  awaiting: awaitingIt,
  resolved: (ticket) => !isOpen(ticket),
  all: () => true,
}

const EMPTY: Record<FilterKey, { title: string; description: string }> = {
  open: {
    title: 'Nothing open',
    description: 'Everything you have raised has been dealt with. Anything new will appear here.',
  },
  mine: {
    title: 'Nothing waiting on you',
    description: 'When IT asks you a question, the ticket will move here so it does not get lost.',
  },
  awaiting: {
    title: 'Nothing with IT right now',
    description: 'Tickets being worked on by the service desk appear here.',
  },
  resolved: {
    title: 'Nothing resolved yet',
    description: 'Once a ticket is finished it moves here, and you can reopen it if it comes back.',
  },
  all: {
    title: 'No tickets yet',
    description: 'Report a problem or request something and you will be able to follow it here.',
  },
}

export function TicketList({ requestsOnly = false }: { requestsOnly?: boolean }) {
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState('')

  const raw = params.get('filter')
  const filter: FilterKey = FILTERS.some((option) => option.key === raw)
    ? (raw as FilterKey)
    : 'open'

  const { data, isLoading } = useQuery({
    queryKey: ['tickets'],
    queryFn: () => api.get<TicketSummary[]>('/tickets/'),
  })

  const tickets = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const rows = (data ?? [])
      .filter((ticket) => (requestsOnly ? isRequest(ticket) : true))
      .filter(PREDICATES[filter])
      .filter(
        (ticket) =>
          needle.length === 0 ||
          ticket.title.toLowerCase().includes(needle) ||
          ticket.ref.toLowerCase().includes(needle),
      )
    return byRecentActivity(rows)
  }, [data, filter, query, requestsOnly])

  const empty = EMPTY[filter]

  return (
    <div>
      <PageHeader
        title={requestsOnly ? 'My requests' : 'My tickets'}
        description={
          requestsOnly
            ? 'Equipment, access and services you have asked for.'
            : 'Everything you have raised, most recently updated first.'
        }
        actions={
          <Button asChild size="sm">
            <Link to={requestsOnly ? '/catalog' : '/new'}>
              <TicketPlus />
              {requestsOnly ? 'Request a service' : 'Raise a ticket'}
            </Link>
          </Button>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          value={filter}
          onValueChange={(value) => {
            // `replace` so filtering does not fill the back button with steps.
            setParams(value === 'open' ? {} : { filter: value }, { replace: true })
          }}
        >
          <TabsList>
            {FILTERS.map((option) => (
              <TabsTrigger key={option.key} value={option.key}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative sm:w-64">
          <label htmlFor="ticket-filter" className="sr-only">
            Filter your tickets
          </label>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id="ticket-filter"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter by subject or reference"
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, row) => (
            <Skeleton key={row} className="h-24 w-full" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <EmptyState
            bare
            icon={Inbox}
            title={query ? `No tickets match “${query}”` : empty.title}
            description={query ? 'Try a different word, or clear the filter.' : empty.description}
            action={
              query ? (
                <Button variant="outline" size="sm" onClick={() => setQuery('')}>
                  Clear filter
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link to={requestsOnly ? '/catalog' : '/new'}>
                    <TicketPlus />
                    {requestsOnly ? 'Request a service' : 'Raise a ticket'}
                  </Link>
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <ul className="space-y-2">
          {tickets.map((ticket) => (
            <li key={ticket.ref}>
              <TicketRow ticket={ticket} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
